import { useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { CheckCircle, ArrowRight, Phone, FileText, Clock } from 'lucide-react';
import { trackLead } from '../services/analytics';

export default function ThankYou() {
  const location = useLocation();
  const submissionId = location.state?.submissionId;
  const serviceType = location.state?.serviceType;
  const leadTracked = useRef(false);

  // Fire Lead event once on successful submission
  useEffect(() => {
    if (!leadTracked.current && submissionId) {
      trackLead({ service: serviceType || 'eligibility_assessment' });
      leadTracked.current = true;
    }
  }, [submissionId, serviceType]);

  return (
    <div data-testid="thank-you-page">
      {/* Hero Section */}
      <section className="bg-[hsl(215,50%,23%)] text-white py-8 md:py-12">
        <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
          <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-2xl md:text-3xl font-bold mb-2 text-white">Assessment Received</h1>
          <p className="text-white/80">
            Thank you for submitting your eligibility assessment.
          </p>
        </div>
      </section>

      <section className="gov-section bg-white">
        <div className="max-w-2xl mx-auto px-4 md:px-6">
          {submissionId && (
            <div className="bg-[hsl(210,20%,96%)] border border-gray-200 rounded-sm p-4 mb-8 text-center">
              <span className="text-gray-600 text-sm">Reference ID: </span>
              <span className="font-mono font-semibold text-[hsl(215,50%,23%)]">{submissionId}</span>
            </div>
          )}

          <p className="text-gray-700 text-center mb-8">
            Our administrative intake team will review your submission and contact you within 1-2 business days.
          </p>

          {/* What's Next */}
          <div className="gov-panel mb-8">
            <h2 className="text-xl font-bold text-[hsl(215,50%,23%)] mb-6">What Happens Next?</h2>
            
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-[hsl(210,20%,96%)] rounded-full flex items-center justify-center flex-shrink-0">
                  <FileText className="h-5 w-5 text-[hsl(215,50%,23%)]" />
                </div>
                <div>
                  <h3 className="font-semibold text-[hsl(215,50%,23%)] mb-1">File Review</h3>
                  <p className="text-gray-600 text-sm">
                    Your submission will be reviewed against statutory criteria by our intake team.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-[hsl(210,20%,96%)] rounded-full flex items-center justify-center flex-shrink-0">
                  <Phone className="h-5 w-5 text-[hsl(215,50%,23%)]" />
                </div>
                <div>
                  <h3 className="font-semibold text-[hsl(215,50%,23%)] mb-1">Contact</h3>
                  <p className="text-gray-600 text-sm">
                    A member of our team will contact you to discuss your eligibility and next steps.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-[hsl(210,20%,96%)] rounded-full flex items-center justify-center flex-shrink-0">
                  <Clock className="h-5 w-5 text-[hsl(215,50%,23%)]" />
                </div>
                <div>
                  <h3 className="font-semibold text-[hsl(215,50%,23%)] mb-1">Timeline</h3>
                  <p className="text-gray-600 text-sm">
                    Expect to hear from us within 1-2 business days. Check your email and phone for our contact.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/">
              <Button className="gov-btn-primary" data-testid="return-home-btn">
                Return Home
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button className="gov-btn-secondary">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
